import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes, faPaperPlane, faHeadset, faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import AdminLayout from "./AdminLayout";
import "./admin.css";

function adminFetch(path, opts = {}) {
  const token = localStorage.getItem("adminToken");
  return fetch(path, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(opts.headers || {}) },
  });
}

const STATUS_TABS = [
  { key: "open",        label: "Open"         },
  { key: "in_progress", label: "In Progress"  },
  { key: "waiting_user",label: "Waiting User" },
  { key: "resolved",    label: "Resolved"     },
  { key: "closed",      label: "Closed"       },
  { key: "all",         label: "All"          },
];

function PriorityBadge({ priority }) {
  return <span className={`adm-badge ${priority || "low"}`}>{priority || "—"}</span>;
}

function TicketPanel({ ticketId, onClose, onDone }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply,   setReply]   = useState("");
  const [sending, setSending] = useState(false);
  const [msg,     setMsg]     = useState({ type: "", text: "" });
  const [newStatus, setNewStatus]     = useState("");
  const [newPriority, setNewPriority] = useState("");
  const [updating, setUpdating]       = useState(false);

  const loadTicket = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`/api/admin/tickets/${ticketId}`);
      const d   = await res.json();
      if (res.ok) { setData(d); setNewStatus(d.status); setNewPriority(d.priority); }
    } finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const res  = await adminFetch(`/api/admin/tickets/${ticketId}/reply`, {
        method: "POST",
        body: JSON.stringify({ message: reply.trim() }),
      });
      const data = await res.json();
      if (res.ok) { setReply(""); setMsg({ type: "success", text: "Reply sent." }); loadTicket(); onDone(); }
      else          setMsg({ type: "error", text: data.message || "Failed to send." });
    } finally { setSending(false); }
  };

  const updateTicket = async () => {
    setUpdating(true);
    try {
      const res = await adminFetch(`/api/admin/tickets/${ticketId}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus, priority: newPriority }),
      });
      const d = await res.json();
      if (res.ok) { setMsg({ type: "success", text: "Ticket updated." }); onDone(); }
      else          setMsg({ type: "error", text: d.message || "Update failed." });
    } finally { setUpdating(false); }
  };

  const t = data;
  return (
    <div className="adm-panel-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="adm-panel">
        <div className="adm-panel-header">
          <h4>Ticket #{ticketId}</h4>
          <button className="adm-panel-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <div className="adm-panel-body">
          {loading && <div className="adm-spinner-wrap"><div className="spinner-border text-secondary" /></div>}
          {msg.text && <div className={`adm-alert ${msg.type}`}>{msg.text}</div>}

          {t && !loading && (
            <>
              <h5 style={{ fontWeight: 700, color: "var(--adm-navy)", marginBottom: ".5rem" }}>{t.subject}</h5>
              <div className="adm-kv-row"><span className="adm-kv-label">Status</span><span className="adm-kv-value"><span className={`adm-badge ${t.status}`}>{t.status?.replace("_", " ")}</span></span></div>
              <div className="adm-kv-row"><span className="adm-kv-label">Priority</span><span className="adm-kv-value"><PriorityBadge priority={t.priority} /></span></div>
              <div className="adm-kv-row"><span className="adm-kv-label">Category</span><span className="adm-kv-value" style={{ textTransform: "capitalize" }}>{t.category?.replace("_", " ") || "—"}</span></div>
              <div className="adm-kv-row"><span className="adm-kv-label">User email</span><span className="adm-kv-value" style={{ fontSize: ".78rem" }}>{t.user_email || "—"}</span></div>
              <div className="adm-kv-row"><span className="adm-kv-label">Created</span><span className="adm-kv-value">{t.created_at ? new Date(t.created_at).toLocaleString() : "—"}</span></div>

              <div style={{ background: "#f8fafc", borderRadius: 8, padding: ".75rem", margin: ".75rem 0", fontSize: ".875rem", border: "1px solid var(--adm-border)" }}>
                <strong style={{ display: "block", marginBottom: ".35rem", fontSize: ".78rem", color: "var(--adm-muted)" }}>ORIGINAL MESSAGE</strong>
                {t.description || t.message}
              </div>

              {/* Thread */}
              {t.replies && t.replies.length > 0 && (
                <div style={{ marginBottom: ".75rem" }}>
                  <p style={{ fontSize: ".78rem", fontWeight: 700, color: "var(--adm-navy)", marginBottom: ".4rem" }}>REPLIES ({t.replies.length})</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                    {t.replies.map((r) => (
                      <div
                        key={r.id}
                        style={{
                          padding: ".6rem .8rem",
                          borderRadius: 8,
                          background: r.author_type === "admin" ? "#e0f2fe" : "#f8fafc",
                          border: "1px solid var(--adm-border)",
                          fontSize: ".84rem",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: ".25rem" }}>
                          <strong style={{ fontSize: ".76rem", color: r.author_type === "admin" ? "#0369a1" : "var(--adm-muted)" }}>
                            {r.author_type === "admin" ? "Admin" : "User"}
                          </strong>
                          <span style={{ fontSize: ".72rem", color: "var(--adm-muted)" }}>
                            {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                          </span>
                        </div>
                        {r.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <hr style={{ margin: ".75rem 0", border: "none", borderTop: "1px solid var(--adm-border)" }} />

              {/* Update status / priority */}
              <div style={{ display: "flex", gap: ".6rem", marginBottom: ".75rem" }}>
                <select className="adm-select" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} style={{ flex: 1 }}>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="waiting_user">Waiting User</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
                <select className="adm-select" value={newPriority} onChange={(e) => setNewPriority(e.target.value)} style={{ flex: 1 }}>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
                <button className="adm-btn primary sm" disabled={updating} onClick={updateTicket}>
                  {updating ? <FontAwesomeIcon icon={faSpinner} spin /> : "Update"}
                </button>
              </div>

              {/* Reply box */}
              <div className="adm-field">
                <label>Reply to user</label>
                <textarea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type your reply…"
                />
              </div>
              <button className="adm-btn primary" disabled={sending || !reply.trim()} onClick={sendReply}>
                {sending ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPaperPlane} />}
                Send Reply
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminSupport() {
  const [statusTab, setStatusTab] = useState("open");
  const [rows,      setRows]      = useState([]);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);

  const load = useCallback(async (tab, pg) => {
    setLoading(true);
    try {
      const res  = await adminFetch(`/api/admin/tickets?status=${tab}&page=${pg}&limit=20`);
      const data = await res.json();
      if (res.ok) { setRows(data.tickets || []); setTotal(data.total || 0); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(statusTab, page); }, [statusTab, page, load]);

  const switchTab = (t) => { setStatusTab(t); setPage(1); setSelected(null); };
  const handleDone = () => { setSelected(null); load(statusTab, page); };
  const totalPages = Math.ceil(total / 20) || 1;

  return (
    <AdminLayout pageTitle="Support Tickets">
      <div className="adm-filter-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            className={`adm-filter-tab${statusTab === t.key ? " active" : ""}`}
            onClick={() => switchTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="adm-card">
        <div className="adm-card-header">
          <h3>Tickets</h3>
          <span style={{ fontSize: ".8rem", color: "var(--adm-muted)" }}>{total} total</span>
        </div>

        {loading && <div className="adm-spinner-wrap"><div className="spinner-border text-secondary" /></div>}

        {!loading && rows.length === 0 && (
          <div className="adm-empty">
            <FontAwesomeIcon icon={faHeadset} />
            <p>No {statusTab === "all" ? "" : statusTab} tickets.</p>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <>
            <div className="adm-table-wrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Subject</th>
                    <th>Category</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id} className="clickable" onClick={() => setSelected(t.id)}>
                      <td style={{ fontSize: ".78rem", color: "var(--adm-muted)" }}>{t.id}</td>
                      <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</td>
                      <td style={{ fontSize: ".82rem", textTransform: "capitalize" }}>{t.category?.replace("_", " ") || "—"}</td>
                      <td><PriorityBadge priority={t.priority} /></td>
                      <td><span className={`adm-badge ${t.status}`}>{t.status?.replace("_", " ")}</span></td>
                      <td style={{ fontSize: ".78rem", color: "var(--adm-muted)" }}>{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</td>
                      <td>
                        <button
                          className="adm-btn ghost sm"
                          onClick={(e) => { e.stopPropagation(); setSelected(t.id); }}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="adm-pagination">
              <span>Page {page} of {totalPages}</span>
              <div className="adm-pag-btns">
                <button className="adm-pag-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
                <button className="adm-pag-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
              </div>
            </div>
          </>
        )}
      </div>

      {selected && <TicketPanel ticketId={selected} onClose={() => setSelected(null)} onDone={handleDone} />}
    </AdminLayout>
  );
}
